import React, { useState, useEffect } from 'react';
import RequesterName from './RequesterName';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const BulkApprovalActions = ({ tickets, onAction }) => {
  const [selected, setSelected] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [selectedContractor, setSelectedContractor] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [actionMode, setActionMode] = useState(null);
  // The thing missing before the action can go, said next to the buttons
  // rather than in an alert() over them.
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    const loadContractors = async () => {
      try {
        const response = await fetch(`${API_BASE}/contractors`);
        if (response.ok) {
          const data = await response.json();
          setContractors(data);
        }
      } catch (err) {
        console.error('Error loading contractors:', err);
      }
    };
    loadContractors();
  }, []);

  const handleSelectAll = () => {
    if (selected.length === tickets.length) {
      setSelected([]);
    } else {
      setSelected(tickets.map(t => t.id));
    }
  };

  const handleSelect = (ticketId) => {
    setSelected(prev =>
      prev.includes(ticketId)
        ? prev.filter(id => id !== ticketId)
        : [...prev, ticketId]
    );
  };

  /**
   * This component no longer talks to the API. It collects a selection and
   * hands it to the page, which makes exactly one request.
   *
   * It used to do both, and that is why bulk approval has never once worked.
   * It fired its own PUT to /approvals/bulk/accept with
   * `{ ticketIds, contractorId }` and then called onAction(data) with the
   * PARSED RESPONSE - while the page's handler is
   * handleBulkApprovalAction(action, ticketIds, data), which goes on to read
   * data.contractorId. So the response object arrived as `action`, `data`
   * arrived undefined, and the page threw
   *
   *   cannot read properties of undefined (reading 'contractorId')
   *
   * every time. And the request this component had already sent used key names
   * the server does not read - it accepts ticket_ids or approvalIds, never
   * ticketIds - so the server had been handed an empty list and quietly done
   * nothing, before the page crashed trying to send the real one.
   *
   * One owner for the request now, and it is the one that was already sending
   * the body the server expects.
   */
  const handleBulkApprove = async () => {
    if (!selectedContractor) {
      setNotice('Please select a contractor');
      return;
    }
    setNotice(null);
    setLoading(true);
    try {
      if (onAction) await onAction('approve', selected, { contractorId: selectedContractor });
      setSelected([]);
      setSelectedContractor('');
      setActionMode(null);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkReject = async () => {
    // The reason is emailed to whoever raised the ticket, so sending one back
    // without saying why is not a thing to allow by accident.
    if (!rejectionReason.trim()) {
      setNotice('Say why these are being sent back — the reason is sent to whoever raised them');
      return;
    }
    setNotice(null);
    setLoading(true);
    try {
      if (onAction) await onAction('reject', selected, { rejectionReason });
      setSelected([]);
      setRejectionReason('');
      setActionMode(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bulk-approval-actions">
      <div className="bulk-header">
        <h3>Bulk Actions</h3>
        {selected.length > 0 && (
          <span className="selected-count">{selected.length} selected</span>
        )}
      </div>

      {notice && <div className="error-message" role="alert">{notice}</div>}

      <div className="bulk-selection-table">
        <table>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selected.length === tickets.length && tickets.length > 0}
                  onChange={handleSelectAll}
                />
              </th>
              <th>Ticket #</th>
              <th>Title</th>
              <th>Requester</th>
              <th>Priority</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map(ticket => (
              <tr key={ticket.id} className={selected.includes(ticket.id) ? 'selected' : ''}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected.includes(ticket.id)}
                    onChange={() => handleSelect(ticket.id)}
                  />
                </td>
                <td><strong>{ticket.ticket_number}</strong></td>
                <td>{ticket.title}</td>
                <td><RequesterName ticket={ticket} fallback="" /></td>
                <td>
                  {/* Without data-priority this fell through to the bare
                      .priority-badge rule, which sets no colour at all - so
                      Critical and Low were the same flat grey pill. The card
                      above the table has always passed it. */}
                  <span
                    className="priority-badge"
                    data-priority={ticket.priority_name?.toLowerCase()}
                  >
                    {ticket.priority_name}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected.length > 0 && (
        <div className="bulk-actions">
          <button
            className="btn btn-success"
            onClick={() => setActionMode('approve')}
            disabled={loading}
          >
            Approve Selected ({selected.length})
          </button>
          <button
            className="btn btn-danger"
            onClick={() => setActionMode('reject')}
            disabled={loading}
          >
            Reject Selected ({selected.length})
          </button>
        </div>
      )}

      {actionMode === 'approve' && (
        <div className="action-modal">
          <div className="modal-content">
            <h4>Approve {selected.length} Tickets</h4>
            <div className="form-group">
              <label>Assign to Contractor:</label>
              <select
                value={selectedContractor}
                onChange={(e) => setSelectedContractor(e.target.value)}
              >
                <option value="">Select a contractor...</option>
                {contractors.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-primary"
                onClick={handleBulkApprove}
                disabled={!selectedContractor || loading}
              >
                {loading ? 'Processing...' : 'Confirm Approval'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setActionMode(null)}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {actionMode === 'reject' && (
        <div className="action-modal">
          <div className="modal-content">
            <h4>Reject {selected.length} Tickets</h4>
            <div className="form-group">
              <label>Rejection Reason:</label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                rows="4"
              ></textarea>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-danger"
                onClick={handleBulkReject}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Confirm Rejection'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setActionMode(null)}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkApprovalActions;
